import { useEffect, useState } from "react";
import type { OklchColor } from "@/document/model/color";
import { formatHexRgbLabel, tryParseColorString } from "@/document/model/colorString";
import { ScrubInput } from "./ScrubInput";

export function PaintColorInput({
  color,
  disabled,
  onCommit,
}: {
  color: OklchColor;
  disabled?: boolean;
  onCommit: (color: OklchColor) => void;
}) {
  const [draft, setDraft] = useState(() => formatHexRgbLabel(color));
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!editing) {
      setDraft(formatHexRgbLabel(color));
    }
  }, [color, editing]);

  function commit(value: string): void {
    const parsed = tryParseColorString(value);
    if (parsed) {
      const next = { ...parsed, alpha: color.alpha };
      onCommit(next);
      setDraft(formatHexRgbLabel(next));
      return;
    }

    setDraft(formatHexRgbLabel(color));
  }

  return (
    <ScrubInput
      value={draft}
      disabled={disabled}
      aria-label="Color value"
      className="min-w-0 flex-1 font-mono text-[11px] text-slate-200 disabled:opacity-40"
      onFocus={() => setEditing(true)}
      onChange={event => setDraft(event.target.value)}
      onBlur={() => {
        setEditing(false);
        commit(draft);
      }}
      onKeyDown={event => {
        if (event.key === "Enter") {
          event.preventDefault();
          event.currentTarget.blur();
        }
        if (event.key === "Escape") {
          event.preventDefault();
          setDraft(formatHexRgbLabel(color));
          setEditing(false);
          event.currentTarget.blur();
        }
      }}
      onClick={event => event.stopPropagation()}
    />
  );
}
